import type { BlockNode, ViewportMode, StyleTargetState } from '../types';
import type { CSSProperties } from 'react';

// Converts camelCase to kebab-case
export function toKebabCase(str: string): string {
  return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
}

// Converts kebab-case to camelCase
export function toCamelCase(str: string): string {
  return str.replace(/-([a-z0-9])/g, (_, g1) => g1.toUpperCase());
}

/**
 * Normalizes raw style records (which could mix camelCase or kebab-case) into React.CSSProperties.
 */
export function normalizeToReactStyles(rawStyles?: Record<string, string | undefined>): CSSProperties {
  if (!rawStyles) return {};
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(rawStyles)) {
    if (value === undefined || value === null || value === '') continue;
    const camelKey = toCamelCase(key);
    result[camelKey] = String(value);
  }

  return result as CSSProperties;
}

/**
 * Resolves the active inline styles for a canvas element depending on:
 * 1. Current viewport (desktop, tablet, mobile) - cascading down
 * 2. Active editor target pseudo-state (hover, active) if the element is currently selected
 */
export function resolveCanvasNodeStyle(
  node: BlockNode,
  viewport: ViewportMode,
  activeState: StyleTargetState = 'default',
  isSelected = false
): CSSProperties {
  const styles = node.styles || { base: {} };

  // 1. Base (Desktop)
  const merged: Record<string, string> = { ...(styles.base || {}) };

  // 2. Tablet cascade
  if (viewport === 'tablet' || viewport === 'mobile') {
    const tabletStyles = styles.tablet || styles.md;
    if (tabletStyles) {
      Object.assign(merged, tabletStyles);
    }
  }

  // 3. Mobile cascade
  if (viewport === 'mobile') {
    if (styles.mobile) {
      Object.assign(merged, styles.mobile);
    }
  }

  // 4. If selected and actively previewing a pseudo-state in Style Manager
  if (isSelected) {
    if (activeState === 'hover' && styles.hover) {
      Object.assign(merged, styles.hover);
    } else if (activeState === 'active' && styles.active) {
      Object.assign(merged, styles.active);
    }
  }

  return normalizeToReactStyles(merged);
}

/**
 * Traverses the node tree and generates a CSS stylesheet string for hover/active pseudo-states
 * so hovering blocks on the canvas immediately reflects custom hover styles.
 */
export function generateCanvasDynamicCSS(root: BlockNode): string {
  const cssRules: string[] = [
    `.wb-artboard-container { container-type: inline-size; }`,
    `.wb-artboard-container *, .wb-root-canvas * { box-sizing: border-box; max-width: 100%; }`,
    `@container (max-width: 768px) {
      .wb-responsive-grid-2, .wb-responsive-grid-3, .wb-responsive-grid-4 {
        grid-template-columns: 1fr !important;
      }
      .wb-responsive-stack {
        flex-direction: column !important;
        align-items: stretch !important;
      }
      .wb-hide-mobile { display: none !important; }
      .wb-show-mobile { display: flex !important; }
    }`,
  ];

  function traverse(node: BlockNode) {
    if (node.styles?.hover && Object.keys(node.styles.hover).length > 0) {
      const decls = Object.entries(node.styles.hover)
        .map(([k, v]) => `${toKebabCase(k)}: ${v} !important;`)
        .join(' ');
      cssRules.push(`[data-node-id="${node.id}"]:hover { ${decls} }`);
    }

    if (node.styles?.active && Object.keys(node.styles.active).length > 0) {
      const decls = Object.entries(node.styles.active)
        .map(([k, v]) => `${toKebabCase(k)}: ${v} !important;`)
        .join(' ');
      cssRules.push(`[data-node-id="${node.id}"]:active { ${decls} }`);
    }

    if (node.children) {
      for (const child of node.children) {
        traverse(child);
      }
    }
  }

  traverse(root);
  return cssRules.join('\n');
}
